import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "./ui/button";
import { Wordmark } from "./BrandBadge";
import {
  BarChart3, Upload, AlertTriangle, Trophy, Swords, LayoutDashboard,
  ChevronRight, ChevronLeft, X, CloudSun, Calendar, ChevronDown,
  Cloud, Sun, CloudRain, Flame, MapPin, Filter, Star, Sparkles, Brain,
} from "lucide-react";

/* ═══ Animated Visuals for each step ═══ */

function WelcomeVisual() {
  return (
    <div className="relative h-28 overflow-hidden rounded-md bg-white border border-black/[0.08] flex items-center justify-center">
      <div className="relative text-center">
        <div className="text-4xl" style={{ animation: 'fadeSlideUp 0.8s ease-out' }}>
          <Wordmark testId="tutorial-wordmark" />
        </div>
        <div className="flex items-center justify-center gap-1.5 mt-2">
          {["CUR","FAL","GRA","LIN","WIN"].map((s, i) => (
            <span key={s} className="px-2 py-0.5 bg-black/[0.05] rounded text-[9px] font-bold text-[#6e6e73]"
              style={{ animation: `fadeSlideUp 0.5s ease-out ${0.3 + i * 0.1}s both` }}>{s}</span>
          ))}
        </div>
      </div>
      <style>{`@keyframes fadeSlideUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

function WhatsNewVisual() {
  return (
    <div className="relative h-28 overflow-hidden rounded-md bg-[#0f0f0d] flex items-center justify-center gap-2 px-4">
      {["Coach chat streams live", "One login screen", "Structured findings", "New look"].map((t, i) => (
        <span key={t} className="px-2.5 py-1 rounded-full bg-white/10 text-white text-[10px] font-semibold whitespace-nowrap"
          style={{ animation: `fadeSlideUp 0.5s ease-out ${0.15 + i * 0.12}s both` }}>{t}</span>
      ))}
    </div>
  );
}

function NavVisual() {
  const items = [
    { icon: BarChart3, label: "KPI", color: "#0071e3" },
    { icon: AlertTriangle, label: "FEEDBACK", color: "#0d9488" },
    { icon: Trophy, label: "TRACKER", color: "#d97706" },
    { icon: Swords, label: "WAR ZONE", color: "#dc2626" },
  ];
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive(p => (p + 1) % items.length), 1200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="h-28 rounded-md bg-slate-900 p-3 flex flex-col justify-between">
      <div className="flex items-center gap-1 bg-slate-800 rounded-md p-1.5">
        {items.map((item, i) => (
          <div key={i} className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded transition-all duration-300 ${
            i === active ? "bg-white/15" : ""
          }`}>
            <item.icon size={11} className={i === active ? "text-white" : "text-slate-500"} />
            <span className={`text-[8px] font-bold uppercase ${i === active ? "text-white" : "text-slate-500"}`}
              style={{ fontFamily: 'Inter, sans-serif' }}>{item.label}</span>
          </div>
        ))}
      </div>
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center transition-all duration-300" key={active}>
          {(() => { const Item = items[active]; return (
            <>
              <Item.icon size={24} style={{ color: Item.color }} className="mx-auto mb-1" />
              <div className="text-[10px] text-white/60 font-bold uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>{Item.label}</div>
            </>
          );})()}
        </div>
      </div>
    </div>
  );
}

function WeatherVisual() {
  const days = [
    { d: "Mon", icon: Sun, temp: 12, color: "#10b981" },
    { d: "Tue", icon: Cloud, temp: 10, color: "#64748b" },
    { d: "Wed", icon: CloudRain, temp: 8, color: "#f59e0b" },
    { d: "Thu", icon: Sun, temp: 14, color: "#10b981" },
    { d: "Fri", icon: CloudSun, temp: 11, color: "#3b82f6" },
    { d: "Sat", icon: Sun, temp: 15, color: "#10b981" },
  ];
  return (
    <div className="h-28 rounded-md overflow-hidden">
      <div className="bg-[#0f172a] px-3 py-2 flex items-center gap-2">
        <Sun size={14} className="text-sky-400" />
        <span className="text-xs font-bold text-white font-mono">12°</span>
        <span className="text-[9px] text-slate-400">Partly cloudy</span>
        <span className="px-1.5 py-0.5 rounded-full text-[7px] font-bold text-white bg-emerald-500 ml-1">GOOD DAY</span>
        <ChevronDown size={10} className="text-slate-500 ml-auto" />
      </div>
      <div className="bg-[#0f172a] px-3 pb-2 flex items-end gap-1.5">
        {days.map((day, i) => (
          <div key={i} className="flex-1 flex flex-col items-center" style={{ animation: `fadeSlideUp 0.4s ease-out ${i * 0.1}s both` }}>
            <span className="text-[7px] font-bold text-slate-500">{day.d}</span>
            <day.icon size={10} className="text-slate-400 my-0.5" />
            <span className="text-[8px] font-bold text-white font-mono">{day.temp}°</span>
            <div className="w-full h-[3px] rounded-full mt-0.5" style={{ backgroundColor: day.color }} />
          </div>
        ))}
      </div>
      <div className="bg-white px-3 py-1.5 flex items-center gap-2 border-t border-slate-100">
        <Calendar size={10} className="text-[#0071e3]" />
        <span className="text-[8px] font-bold text-slate-700 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>Business Calendar</span>
        <div className="flex items-center gap-0.5 ml-1">
          <Flame size={8} className="text-orange-500" />
          <span className="text-[7px] text-orange-500 font-bold">3 events</span>
        </div>
      </div>
      <style>{`@keyframes fadeSlideUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

function KPIVisual() {
  const [vals, setVals] = useState([0, 0, 0, 0]);
  useEffect(() => {
    const targets = [31174, 28.5, 14.2, 4];
    const t = setTimeout(() => setVals(targets), 300);
    return () => clearTimeout(t);
  }, []);
  const cards = [
    { label: "WTD SALES", val: `£${vals[0].toLocaleString()}`, color: "#0071e3" },
    { label: "LABOUR %", val: `${vals[1]}%`, color: vals[1] > 30 ? "#dc2626" : "#10b981" },
    { label: "AVG OTD", val: `${vals[2]} min`, color: vals[2] > 15 ? "#f59e0b" : "#10b981" },
    { label: "DAYS", val: `${vals[3]}/7`, color: "#8b5cf6" },
  ];
  return (
    <div className="h-28 rounded-md bg-slate-50 p-2 space-y-2">
      <div className="flex items-center gap-1.5">
        <div className="px-2 py-0.5 bg-[#0071e3] rounded text-[8px] font-bold text-white">FAL</div>
        <div className="px-2 py-0.5 bg-amber-50 border border-amber-300 rounded text-[8px] font-bold text-amber-800">1st</div>
        <div className="flex gap-0.5 ml-1">
          {[1,2,3,4,5].map(i => <div key={i} className={`w-2 h-2 rounded-full ${i===1 ? "bg-amber-500 ring-1 ring-white" : "bg-slate-200"}`} />)}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {cards.map((c, i) => (
          <div key={i} className="bg-white rounded border border-slate-200 px-2 py-1.5 text-center"
            style={{ animation: `fadeSlideUp 0.5s ease-out ${i * 0.15}s both` }}>
            <div className="text-[7px] font-bold text-slate-400 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>{c.label}</div>
            <div className="text-[11px] font-black font-mono transition-all duration-700" style={{ color: c.color }}>{c.val}</div>
          </div>
        ))}
      </div>
      <style>{`@keyframes fadeSlideUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

function ChartVisual() {
  const points = [20, 35, 28, 45, 52, 48, 65];
  return (
    <div className="h-28 rounded-md bg-white border border-slate-200 p-2.5 space-y-1.5">
      <div className="flex items-center gap-1.5">
        <ChevronDown size={10} className="text-[#0071e3]" />
        <span className="text-[8px] font-bold text-[#0071e3] uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>Performance Charts</span>
        <span className="text-[7px] text-slate-400 ml-auto">Click to expand</span>
      </div>
      <svg viewBox="0 0 200 60" className="w-full h-16">
        <defs>
          <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0071e3" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#0071e3" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`M0,${60-points[0]} ${points.map((p,i) => `L${(i/(points.length-1))*200},${60-p}`).join(' ')} L200,60 L0,60 Z`}
          fill="url(#chartFill)">
          <animate attributeName="opacity" from="0" to="1" dur="1s" fill="freeze" />
        </path>
        <polyline
          points={points.map((p,i) => `${(i/(points.length-1))*200},${60-p}`).join(' ')}
          fill="none" stroke="#0071e3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="300" strokeDashoffset="300">
          <animate attributeName="stroke-dashoffset" from="300" to="0" dur="1.5s" fill="freeze" />
        </polyline>
        {points.map((p,i) => (
          <circle key={i} cx={(i/(points.length-1))*200} cy={60-p} r="3" fill="#0071e3" opacity="0">
            <animate attributeName="opacity" from="0" to="1" dur="0.3s" begin={`${0.5 + i*0.15}s`} fill="freeze" />
          </circle>
        ))}
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d,i) => (
          <text key={i} x={(i/6)*200} y="58" fontSize="6" fill="#94a3b8" textAnchor="middle">{d}</text>
        ))}
      </svg>
    </div>
  );
}

function FeedbackVisual() {
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    const seq = ["all", "FAL", "CUR", "all"];
    let i = 0;
    const t = setInterval(() => { i = (i + 1) % seq.length; setFilter(seq[i]); }, 1500);
    return () => clearInterval(t);
  }, []);
  const metrics = filter === "FAL" ? [33, 67, 0] : filter === "CUR" ? [50, 25, 25] : [58, 25, 17];
  return (
    <div className="h-28 rounded-md bg-white border border-slate-200 p-2.5 space-y-1.5">
      <div className="flex items-center gap-1">
        <span className="text-[8px] font-bold text-slate-800 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>OSAT Metrics</span>
        <div className="flex gap-1 ml-auto">
          {["all", "FAL", "CUR"].map(f => (
            <span key={f} className={`px-1.5 py-0.5 rounded-full text-[7px] font-bold transition-all duration-300 ${
              filter === f ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-400"
            }`}>{f === "all" ? "ALL" : f}</span>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {[
          { label: "Satisfied", color: "#10b981", pct: metrics[0] },
          { label: "Not Highly", color: "#f59e0b", pct: metrics[1] },
          { label: "Not Satisfied", color: "#ef4444", pct: metrics[2] },
        ].map((m, i) => (
          <div key={i} className="text-center rounded p-1.5" style={{ backgroundColor: m.color + "10", border: `1px solid ${m.color}30` }}>
            <div className="text-sm font-black font-mono transition-all duration-500" style={{ color: m.color }}>{m.pct}%</div>
            <div className="text-[6px] font-bold text-slate-400 uppercase">{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrackerVisual() {
  const weeks = [17.2, 16.8, 17.5, 16.1, 15.9, 16.3, 15.5, 16.0];
  return (
    <div className="h-28 rounded-md bg-white border border-slate-200 p-2.5 space-y-1.5">
      <div className="text-[8px] font-bold text-slate-800 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>Weekly Bonus Tracker</div>
      <div className="flex items-end gap-1 h-16">
        {weeks.map((w, i) => {
          const h = ((w - 15) / 3) * 100;
          const color = w <= 16 ? "#10b981" : w <= 17 ? "#f59e0b" : "#ef4444";
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
              <div className="w-full rounded-t transition-all duration-500" style={{
                height: `${Math.max(10, h)}%`, backgroundColor: color,
                animation: `growUp 0.5s ease-out ${i * 0.1}s both`
              }} />
              <span className="text-[6px] text-slate-400 font-mono mt-0.5">W{i+1}</span>
            </div>
          );
        })}
      </div>
      <style>{`@keyframes growUp { from { height: 0; } }`}</style>
    </div>
  );
}

function WarzoneVisual() {
  const stores = [
    { code: "FAL", sales: 85, color: "#4a8db5" },
    { code: "CUR", sales: 72, color: "#c2545e" },
    { code: "WIN", sales: 90, color: "#8e7cb8" },
    { code: "GRA", sales: 68, color: "#5a9e6f" },
    { code: "LIN", sales: 78, color: "#c9943a" },
  ];
  return (
    <div className="h-28 rounded-md bg-slate-900 p-2.5 space-y-1">
      <div className="flex items-center gap-1.5">
        <Swords size={10} className="text-red-500" />
        <span className="text-[8px] font-bold text-white uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>War Zone</span>
      </div>
      <div className="space-y-1">
        {stores.sort((a,b) => b.sales - a.sales).map((s, i) => (
          <div key={s.code} className="flex items-center gap-1.5" style={{ animation: `fadeSlideUp 0.4s ease-out ${i * 0.1}s both` }}>
            <span className="text-[8px] font-bold text-white w-6">{s.code}</span>
            <div className="flex-1 h-3 bg-white/10 rounded overflow-hidden">
              <div className="h-full rounded transition-all duration-1000" style={{ width: `${s.sales}%`, backgroundColor: s.color, animation: `growWidth 1s ease-out ${i * 0.15}s both` }} />
            </div>
            <span className="text-[8px] font-mono text-white/60 w-6 text-right">{s.sales}%</span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes fadeSlideUp { from { opacity:0; transform:translateY(5px); } to { opacity:1; transform:translateY(0); } }
        @keyframes growWidth { from { width: 0; } }
      `}</style>
    </div>
  );
}

function SubmitVisual() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep(p => (p + 1) % 4), 1200);
    return () => clearInterval(t);
  }, []);
  const phases = ["Selecting store...", "Uploading images...", "OCR processing...", "Submission complete!"];
  const icons = [MapPin, Upload, BarChart3, Star];
  const colors = ["#0071e3", "#f59e0b", "#8b5cf6", "#10b981"];
  const CurIcon = icons[step];
  return (
    <div className="h-28 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center">
      <div className="text-center transition-all duration-300" key={step}>
        <div className="w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-2"
          style={{ backgroundColor: colors[step] + "15", border: `2px solid ${colors[step]}40` }}>
          <CurIcon size={20} style={{ color: colors[step] }} />
        </div>
        <div className="text-[10px] font-bold text-slate-700">{phases[step]}</div>
        <div className="flex gap-1 justify-center mt-1.5">
          {[0,1,2,3].map(i => (
            <div key={i} className={`w-8 h-1 rounded-full transition-all duration-300 ${i <= step ? "bg-[#0071e3]" : "bg-slate-200"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function AdminVisual() {
  return (
    <div className="h-28 rounded-md bg-white border border-slate-200 p-2.5">
      <div className="grid grid-cols-3 gap-2 h-full">
        {[
          { n: "24", label: "Submissions", color: "#0071e3" },
          { n: "3", label: "Pending", color: "#f59e0b" },
          { n: "5", label: "Stores", color: "#10b981" },
        ].map((s, i) => (
          <div key={i} className="bg-slate-50 rounded border border-slate-100 flex flex-col items-center justify-center"
            style={{ animation: `fadeSlideUp 0.5s ease-out ${i * 0.15}s both` }}>
            <div className="text-xl font-black font-mono" style={{ color: s.color }}>{s.n}</div>
            <div className="text-[7px] font-bold text-slate-400 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>{s.label}</div>
          </div>
        ))}
      </div>
      <style>{`@keyframes fadeSlideUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

function BonusVisual() {
  const [pos, setPos] = useState(0);
  const [mouth, setMouth] = useState(true);
  useEffect(() => {
    const t = setInterval(() => { setPos(p => (p + 1) % 20); setMouth(m => !m); }, 150);
    return () => clearInterval(t);
  }, []);
  const msg = "Sell More Pizzas!".split("");
  return (
    <div className="h-28 rounded-md overflow-hidden" style={{ background: "#0f172a" }}>
      <div className="px-3 pt-2">
        <div className="relative h-6 mb-2">
          {msg.map((ch, i) => {
            const eaten = i < pos;
            return ch !== " " ? (
              <span key={i} className="absolute text-[10px] font-black transition-all duration-100"
                style={{ left: `${((i+1)/(msg.length+1))*100}%`, color: "#fbbf24", opacity: eaten ? 0 : 1, transform: `scale(${eaten ? 0 : 1})`,
                  textShadow: "0 0 4px rgba(251,191,36,0.4)", fontFamily: "Inter, sans-serif" }}>{ch}</span>
            ) : null;
          })}
          <div className="absolute top-1/2 -translate-y-1/2 transition-all" style={{ left: `${(pos/20)*88}%`, transitionDuration: "150ms" }}>
            <svg width="20" height="20" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="14" fill="#4a8db5" />
              {mouth && <path d="M18 18 L33 7 L33 29 Z" fill="#0f172a" />}
              <circle cx="12" cy="11" r="2.5" fill="#0f172a" />
            </svg>
          </div>
        </div>
      </div>
      <div className="px-3 pb-2 text-center">
        <div className="inline-flex items-baseline gap-1.5">
          <span className="text-lg font-black font-mono text-[#4a8db5] animate-pulse" style={{ fontFamily: 'Inter, sans-serif' }}>£31,174</span>
          <span className="text-xs text-white/25 font-bold">/</span>
          <span className="text-xs font-bold text-white/40 font-mono" style={{ fontFamily: 'Inter, sans-serif' }}>£195,173</span>
        </div>
        <div className="text-[9px] text-white/25 mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>Quarterly Sales · Q2 2026</div>
        <div className="flex justify-center gap-2 mt-1.5">
          <span className="text-[8px] font-black text-amber-400 bg-amber-400/15 px-1.5 py-0.5 rounded-sm">L1</span>
          <span className="text-[8px] font-bold text-white/40">+8.2%</span>
        </div>
      </div>
    </div>
  );
}

function PDFVisual() {
  const [building, setBuilding] = useState(true);
  useEffect(() => { const t = setTimeout(() => setBuilding(false), 2000); return () => clearTimeout(t); }, []);
  return (
    <div className="h-28 rounded-md bg-white border border-slate-200 p-2.5 flex items-center gap-3">
      <div className="w-20 h-24 rounded border border-slate-300 bg-slate-50 flex flex-col p-1.5 overflow-hidden relative">
        {building && <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/80 z-10" style={{ animation: "pdfScan 2s ease-out forwards" }} />}
        <div className="w-6 h-1 bg-[#0071e3] rounded-full mb-1" />
        <div className="w-full h-0.5 bg-slate-200 mb-0.5" />
        <div className="w-full h-0.5 bg-slate-200 mb-0.5" />
        <div className="w-3/4 h-0.5 bg-slate-200 mb-1.5" />
        <div className="flex gap-1 mb-1">
          <div className="w-1/2 h-4 bg-emerald-100 rounded-sm" />
          <div className="w-1/2 h-4 bg-amber-100 rounded-sm" />
        </div>
        <div className="w-full h-3 bg-slate-100 rounded-sm mb-0.5" />
        <div className="w-full h-3 bg-slate-100 rounded-sm" />
      </div>
      <div className="flex-1 space-y-1.5">
        <div className="text-[9px] font-bold text-[#0071e3] uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>Weekly Store Report</div>
        <div className="space-y-0.5">
          {["Sales Summary", "Daily Breakdown", "OTG Analysis", "Complaint Review", "Action Plan"].map((s, i) => (
            <div key={i} className="flex items-center gap-1.5" style={{ animation: `fadeSlideUp 0.3s ease-out ${i * 0.15}s both` }}>
              <div className="w-1 h-1 rounded-full bg-emerald-500" style={{ opacity: building ? 0 : 1, transition: "opacity 0.3s", transitionDelay: `${i * 0.2}s` }} />
              <span className="text-[8px] text-slate-500">{s}</span>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes fadeSlideUp { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pdfScan { from { transform: translateY(-100%); } to { transform: translateY(100%); opacity: 0; } }
      `}</style>
    </div>
  );
}

const VISUAL_MAP = {
  whatsnew: WhatsNewVisual,
  welcome: WelcomeVisual,
  nav: NavVisual,
  weather: WeatherVisual,
  kpi: KPIVisual,
  charts: ChartVisual,
  bonus: BonusVisual,
  pdf: PDFVisual,
  feedback: FeedbackVisual,
  tracker: TrackerVisual,
  warzone: WarzoneVisual,
  submit: SubmitVisual,
  admin: AdminVisual,
};

/* ═══ STORE USER TUTORIAL ═══ */
const STORE_TUTORIAL = [
  { title: "Welcome, Store Manager!", icon: null, visual: "welcome",
    content: [
      "This is your operations hub — built specifically for store managers like you.",
      "Track your daily sales, monitor KPIs, see customer feedback, hit bonus targets, and compete against other stores — all in one place.",
      "No spreadsheets, no manual data entry. Just snap photos of your daily reports and the system handles everything automatically.",
    ],
    tip: "This tour shows automatically only the first time on this device. You can re-open it anytime from Settings \u2192 Feedback & Help (\u201cShow welcome tour again\u201d)." },

  { title: "What's New — September 2026", icon: Sparkles, visual: "whatsnew",
    content: [
      "ONE LOGIN — just a username and password. Your username is your store code in lowercase (e.g. \u201ccur\u201d) unless your Super Admin renamed it; your password is your store PIN.",
      "DOMI-COACH CHAT — \u201cAsk Your Coach\u201d now answers live, word by word, only looks at the data your question is about, and tells you plainly when a figure isn\u2019t in yet instead of guessing.",
      "THUMBS UP / DOWN — rate every Coach answer so we can keep improving it.",
      "CLEARER COACH REPORTS — every recommendation shows Finding \u2192 Evidence \u2192 Root cause \u2192 Action with a priority tier (Food Safety \u203a Labour \u203a OTD \u203a OSAT).",
      "NEW LOOK — the DomiOps wordmark with the moving O replaces the old ring logo across the app.",
    ],
    tip: "You\u2019ll see this tour once after each big update. Re-open it any time from Settings \u2192 Feedback & Help." },

  { title: "How to Navigate", icon: null, visual: "nav",
    content: [
      "On a computer the menu is the slim bar on the left — hover it to expand. On your phone, tap the menu icon (three lines) top-right.",
      "NEW SUBMISSION — Upload your daily photos here (you'll do this every day).",
      "DOMI-COACH — Your AI coach: today's structured report plus the \u201cAsk Your Coach\u201d chat.",
      "PERFORMANCE — Your daily command centre: KPIs, charts, Labour Plan, weather and predictions.",
      "FEEDBACK — Customer complaints and OSAT satisfaction data.",
      "KPI TRACKER — Weekly bonus tracking across all stores.",
      "SALES BUILDING — Local marketing ideas and campaigns.",
      "BEYOND OPERATIONS — Domi-Heist leaderboard, Domi-Grill streak race and Domi-Vision pizza quality.",
      "DATING TOOL — Shelf-life / dating guide lookups.",
      "HELP — Settings, anonymous feedback and this tour.",
    ],
    tip: "Log out from the bottom of the left menu. Anonymous visitors log in from the big \u201cLog in\u201d button on the home page." },

  { title: "Domi-Coach — Your Daily Report", icon: Brain, visual: "kpi",
    content: [
      "Open DOMI-COACH for a report written for your store from your own numbers.",
      "Each recommendation is structured: FINDING (metric + number) \u2192 EVIDENCE (the exact days/figures) \u2192 ROOT CAUSE \u2192 ACTION (one assignable step with a number) \u2192 PRIORITY TIER.",
      "Tiers always run Food Safety \u203a Labour \u203a OTD \u203a OSAT — do the top tier first.",
      "If a root cause says \u201cLikely cause (unconfirmed)\u201d the data hints at it but doesn\u2019t prove it — check on the floor before acting.",
    ],
    tip: "Reports refresh in the background as new data lands — you never wait on a spinner." },

  { title: "Ask Your Coach — Live Chat", icon: Brain, visual: "kpi",
    content: [
      "Under the report, type any question: \u201cHow\u2019s my labour this week?\u201d, \u201cWhich OSAT score is weakest?\u201d, \u201cWhat do I fix first?\u201d",
      "Answers stream in live and only use the data your question is about (default: your store, last 7 days). Say \u201ctrend\u201d or \u201cmonth\u201d to widen it.",
      "If a figure isn\u2019t in yet, the Coach says so — it never estimates.",
      "Tap \uD83D\uDC4D or \uD83D\uDC4E under an answer to tell us if it helped.",
    ],
    tip: "You get 5 questions a day — make them count, or ask your Area Manager for more." },

  { title: "Your Daily Task: Submit Data", icon: Upload, visual: "submit",
    content: [
      "Every day, you need to submit your store's daily figures. Here's exactly how:",
      "1. Click 'NEW SUBMISSION' in the nav bar.",
      "2. Select your store code from the dropdown (e.g., FAL for Falkirk).",
      "3. Type your name (the manager on shift today).",
      "4. Make sure today's date is selected.",
    ],
    tip: "Do this at the end of every shift — it takes less than 2 minutes." },

  { title: "Uploading Your 5 Photos", icon: Upload, visual: "submit",
    content: [
      "You need to upload 5 photos from your store's screens:",
      "Photo 1: DAILY SUMMARY — The main end-of-day financial summary screen.",
      "Photo 2: CREDIT CARD MACHINE — The card terminal's daily total (if you have 2 machines, upload both).",
      "Photo 3: E-DEBIT — Your electronic debit report screen.",
      "Photo 4: SERVICE TIMES — The delivery service times screen showing OTD.",
      "Photo 5: JUST EAT — Your Just Eat daily summary (if applicable).",
    ],
    tip: "Take each photo in good lighting. Make sure the FULL screen is visible — don't crop or cut off any numbers." },

  { title: "After Submitting", icon: Upload, visual: "submit",
    content: [
      "Once all 5 photos are uploaded, tap the green 'SUBMIT & PROCESS' button at the bottom.",
      "The system uses AI to read every number from your photos automatically — you don't type anything.",
      "Wait about 10-15 seconds while it processes. You'll see a green 'Submission Complete!' banner when it's done.",
      "You can share the results instantly on WhatsApp by tapping the share button.",
      "That's it! Your data is now live and feeding into all the KPI dashboards.",
    ],
    tip: "If a photo is unclear, the system will still try to process it — but clearer photos = more accurate results." },

  { title: "Your KPI Page — Overview", icon: BarChart3, visual: "kpi",
    content: [
      "After submitting, go to 'KPI' in the nav. This is where you see how your store is performing.",
      "At the very top, you'll see your store code (e.g., FAL) and your ranking badge (1st, 2nd, 3rd, 4th, or 5th out of all stores).",
      "Next to the ranking, coloured dots show your OSA audit ratings from the last 3 visits.",
      "Below that are your KPI summary cards in a row — let me explain each one on the next page.",
    ],
    tip: "Check this page every day after submitting. It's the fastest way to know how you're doing." },

  { title: "Understanding Your KPI Cards", icon: BarChart3, visual: "kpi",
    content: [
      "Here's what each KPI card means:",
      "WTD SALES (£) — Your total sales this week so far. Higher is better.",
      "LABOUR % — Staff wages as a percentage of sales. LOWER is better (target is usually under 28-30%).",
      "AVG OTD — Average 'Out The Door' time for deliveries in minutes. LOWER is better.",
      "DAYS REPORTED — How many days this week you've submitted data (out of 7).",
      "CONCERNS — Number of customer complaints this week. LOWER is better.",
      "OSAT — Overall Satisfaction score from customer surveys (percentage). HIGHER is better.",
      "OSA — Operations Standards Assessment score out of 150 points. HIGHER is better.",
    ],
    tip: "Green numbers = you're on target. Red numbers = that metric needs attention today." },

  { title: "Weather & Order Predictions", icon: CloudSun, visual: "weather",
    content: [
      "Right above your KPI cards, there's a dark weather bar showing the LIVE weather for your exact store location.",
      "It shows: current temperature, weather description, and a coloured prediction badge:",
      "'Peak Day' (green) — Sunny + warm, especially weekends = expect lots of orders.",
      "'Good Day' (blue) — Nice conditions, steady orders.",
      "'Slight Dip' (amber) — Rain or cold = slightly fewer orders than normal.",
      "'Low Traffic' (red) — Heavy rain, snow, or storms = expect reduced orders.",
    ],
    tip: "Click the weather bar to expand a full 7-day forecast. Plan your staffing based on what's coming." },

  { title: "Business Calendar — Busy Events", icon: Calendar, visual: "weather",
    content: [
      "Below the weather is the Business Calendar bar. Click it to expand.",
      "It shows upcoming events in the next 21 days that typically bring MORE orders: football matches (Premier League, Champions League, Scottish Premiership), rugby (Six Nations, Edinburgh Rugby), UFC fights, boxing, pay weekends, bank holidays, and local events.",
      "Each event has a category tag (Football, Rugby, UFC, etc.) and an impact badge (Peak/High/Moderate).",
      "Events with a fire icon are the biggest ones — Champions League, World Cup, Super Bowl, etc.",
    ],
    tip: "See a 'Peak' event on Saturday? Tell your team to prep extra dough and schedule more drivers." },

  { title: "Performance Charts", icon: ChevronDown, visual: "charts",
    content: [
      "On the KPI page, scroll down and look for 'PERFORMANCE CHARTS'. Click this header to expand it.",
      "You'll see line charts showing your Sales, Labour %, and OTD trends across each day of the week.",
      "The solid line is your ACTUAL performance. The dotted line is your TARGET. If the solid line is above the target on sales (good) or below on labour % (good), you're winning.",
      "These charts make it very easy to spot which day of the week you need to improve.",
    ],
    tip: "Charts are hidden by default to keep the page clean. Click the header anytime to see them." },

  { title: "Daily Breakdown Table", icon: ChevronDown, visual: "charts",
    content: [
      "Below the charts is the 'DAILY BREAKDOWN' table showing every day of the week.",
      "Each row shows: Day | Sales (£) | Target (£) | Variance (+/-) | Labour % | OTD (minutes) | GPS ADT (mm:ss).",
      "Variance tells you if you were above or below target. Green = above target (good). Red = below.",
      "Below this table is the OTG section showing your Out The Gate times with a 13-week rolling average.",
    ],
    tip: "Look at your variance column — which days are red? Focus your effort on improving those specific days." },

  { title: "Your Bonus Progress (PacMan)", icon: Trophy, visual: "bonus",
    content: [
      "At the bottom of the KPI page, you'll see a floating dark bar — this is the quarterly bonus tracker.",
      "It shows a PacMan character eating through the letters of 'Sell More Pizzas Have More Fun!'",
      "As your store's quarterly sales grow, PacMan eats more letters. The bar shows your current sales vs your quarterly target (e.g., £31,174 / £195,173).",
      "Bonus levels (L1, L2, L3...) unlock automatically as you hit milestones. Each level means more bonus money.",
      "When PacMan finishes eating all the letters — the screen flashes 'HURRRR!!!' — celebrating your milestone!",
    ],
    tip: "Tap the bonus bar to dismiss it temporarily. It'll reappear next time you visit KPI." },

  { title: "Customer Feedback — Complaints", icon: AlertTriangle, visual: "feedback",
    content: [
      "Go to 'FEEDBACK' in the nav. This page has two tabs at the top: Complaints and OSAT.",
      "The Complaints tab shows all customer complaints for YOUR store (you only see your own).",
      "Complaints are grouped by date and colour-coded by category: Late Delivery (red), Missing Items (orange), Food Quality (amber), Cold Food (blue), Wrong Order (purple), etc.",
      "At the top, the analytics dashboard shows your total complaints, category trends over time, and any repeat issues.",
    ],
    tip: "Look for the 'Repeat Issues' section — if the same complaint type appears 3+ weeks in a row, it needs urgent attention." },

  { title: "Customer Feedback — OSAT", icon: AlertTriangle, visual: "feedback",
    content: [
      "Click the 'OSAT' tab on the Feedback page.",
      "You'll see three big metric cards: % Highly Satisfied (green), % Not Highly Satisfied (amber), % Not Satisfied (red).",
      "These percentages are calculated from your store's customer survey responses.",
      "Below the metrics are the actual customer comments, grouped by satisfaction level and colour-coded.",
      "Read the comments carefully — they tell you exactly what customers love and what frustrates them about your store.",
    ],
    tip: "The 'Not Satisfied' comments are gold — they tell you exactly what to fix to improve your OSAT score." },

  { title: "Download Your PDF Report", icon: BarChart3, visual: "pdf",
    content: [
      "On the KPI page, look for the 'DOWNLOAD REPORT' button in the top-right area.",
      "Click it to generate a professional weekly report PDF for your store.",
      "The report includes: sales summary, daily breakdown, OTG analysis, complaint review, and an auto-generated action plan with specific improvement suggestions.",
      "Print it or share it with your team at the next shift briefing. Everything is formatted and ready to go.",
    ],
    tip: "The action plan auto-generates suggestions based on YOUR actual numbers — e.g., 'Focus on reducing OTD on Saturdays'." },

  { title: "Weekly Tracker", icon: Trophy, visual: "tracker",
    content: [
      "Go to 'TRACKER' in the nav. This shows your weekly performance over time in a grid.",
      "Each row is a different metric (Labour %, OTD, OTG, etc.) and each column is a week.",
      "Cells are colour-coded: GREEN = on target (good). AMBER = close to target. RED = over target (needs work).",
      "The 'DROP' column tells you exactly how much you need to improve to reach your target.",
      "Look for patterns — if a metric has been red for 3+ weeks, it needs a serious plan to fix.",
    ],
    tip: "Your goal is simple: turn red cells into green cells week after week. That's how you earn bonuses." },

  { title: "War Zone — Beat Other Stores!", icon: Swords, visual: "warzone",
    content: [
      "Go to 'DOMI-HEIST' in the nav (formerly 'War Zone'). This is the competitive leaderboard showing all stores in a live combat-style ranking.",
      "It's a heatmap grid — each row is a store, each column is a metric (Sales, OTD, Labour %, OTG, etc.).",
      "The best store in each metric glows green. The worst is red. Your goal: have the most green cells.",
      "This updates in real-time as stores submit data. Check it throughout the week to see where you stand.",
    ],
    tip: "Screenshot Domi-Heist and share it in your team WhatsApp group to spark competition!" },

  { title: "Domi-Grill — Daily Streak Race", icon: Flame, visual: "warzone",
    content: [
      "Go to 'DOMI-GRILL' in the nav. This is a year-long rolling weekly race where every store tries to hit their daily sales target.",
      "Hit your daily target → your grill stays lit and your streak grows. Miss it → flame goes out, streak resets.",
      "At the end of each week the store with the longest streak is crowned champion. Weekly bragging rights + a permanent badge on your profile.",
      "The leaderboard shows live flame icons next to each store. The taller the flame, the longer the streak.",
    ],
    tip: "Domi-Grill rewards consistency, not just one big day. A 6-day streak beats a single record-breaking Saturday." },

  { title: "Domi-Vision — AI Pizza Quality", icon: BarChart3, visual: "kpi",
    content: [
      "Go to 'DOMI-VISION' in the nav. This is your live AI camera-quality dashboard.",
      "An overhead camera at your cut-table scores every pizza as Great, Coached, or Remake. Admin uploads the daily scores and the weekly photo PDF.",
      "Your dashboard shows: this week's Great %, a 12-week trend, defect breakdown (anchor, bubble, crust, size, topping), camera quality stats, and a photo gallery of every scored pizza this week.",
      "Compare yourself against the 92% Great target and the other stores' rankings.",
    ],
    tip: "Got a low Great % this week? Check the Defect Fingerprint radar — it tells you exactly which defect type to coach on." },

  { title: "Pizza of the Week Banner", icon: Star, visual: "warzone",
    content: [
      "Every Monday, the top of your Performance page may show a red/blue 'Pizza of the Week' banner.",
      "It celebrates whichever store had the biggest week-over-week jump in Great % — or the cleanest week with zero remakes.",
      "If your store wins, your name is up in lights for the whole network to see. Dismiss the banner with the X — it'll come back next week with a new winner.",
      "No data yet? The banner stays hidden until your camera has scored enough pizzas to make a fair call.",
    ],
    tip: "Want to win? Tighten cuts on Saturdays and reduce 'topping' coachables — those move the needle fastest." },

  { title: "Help & Anonymous Feedback", icon: Star, visual: "feedback",
    content: [
      "Click 'HELP' in the nav anytime. This is a 10-question survey about the app itself — fully anonymous, no store_code or IP captured.",
      "Tell us what's working, what's annoying, what you wish we'd build next.",
      "All responses go straight to the dev team. We read every single one and prioritise the most-requested ideas.",
      "If you've spotted a bug, this is the fastest way to flag it.",
    ],
    tip: "Be brutal — we'd rather hear what's broken than what's polite. The harshest feedback usually leads to the best fixes." },
];

/* ═══ ADMIN TUTORIAL ═══ */
const ADMIN_TUTORIAL = [
  { title: "Welcome, Admin!", icon: null, visual: "welcome",
    content: [
      "You have full control over the operations hub. You can see all 5 stores, manage data, set targets, review submissions, and track performance.",
      "Store managers see a simpler version — they only see their own store. You see everything.",
      "Let's walk through every admin feature so you can manage the entire operation efficiently.",
    ],
    tip: "This tour shows automatically only the first time on this device. You can re-open it anytime from Settings \u2192 Feedback & Help (\u201cShow welcome tour again\u201d)." },

  { title: "What's New — September 2026", icon: Sparkles, visual: "whatsnew",
    content: [
      "ONE LOGIN SCREEN for everyone — username + password; back-office accounts still get the 6-digit Token ID step. The old Store / Back Office / Trainee tabs are gone.",
      "STORE LOGINS — in Settings \u2192 Users & Logins you set each store\u2019s username, reset its password (8+ chars, upper/lower/number/special) and enable or disable its login. Admin users can be deactivated or unlocked there too.",
      "DOMI-COACH — Ask Operations and every store\u2019s Coach chat now stream live, scope the data to the question (Area default: all 5 stores, last 7 days) and say so by store name when data is missing. Thumbs up/down on every answer.",
      "STRUCTURED REPORTS — Finding \u2192 Evidence \u2192 Root cause (\u201cLikely cause (unconfirmed)\u201d when not proven) \u2192 Action \u2192 Priority tier, with per-store findings never averaged away, generated with extended thinking plus a self-check pass. \u201cRefresh Coach\u201d now runs in the background.",
      "LABOUR PLAN — drop all your Insider / Driver / Schedule .xls files at once; each file\u2019s store and type is detected from its name and you confirm a checklist before saving.",
      "SETTINGS — reorganised like iPhone Settings: General, Accounts & Security, Data Center, AI, Beyond Operations.",
    ],
    tip: "Security in this release: store-scoped submission access, cookie-based Gmail OAuth, 2FA attempt throttling and a full dependency vulnerability sweep." },

  { title: "Navigation — What's Where", icon: null, visual: "nav",
    content: [
      "The slim left bar (hover to expand; hamburger on mobile) holds everything:",
      "DOMI-COACH — Area report across all 5 stores, per-store drill-down, and the \u201cAsk Operations\u201d chat.",
      "DATA INPUT — Submission stats and the paste tools for OTG / OSA / OSAT / Complaints.",
      "SUBMISSIONS — Review and approve/reject daily photo submissions from all stores.",
      "PERFORMANCE — Any store\u2019s KPIs or \u201cAll Stores\u201d comparison, Labour Plan multi-upload, predictions.",
      "FEEDBACK — Complaints + OSAT + the yearly Complaints Insights dashboard.",
      "KPI TRACKER — Weekly performance grid with bonus drop-out tracking.",
      "SALES BUILDING · BEYOND OPERATIONS (Domi-Heist, Domi-Grill, Domi-Vision) · DATING TOOL.",
      "HELP — Settings hub: Feedback, Appearance, Users & Logins, Device Log, Data Center, AI Logs, Pizza Maker.",
    ],
    tip: "Start your day on Domi-Coach, then Submissions to review overnight uploads." },

  { title: "Domi-Coach — Area Report & Ask Operations", icon: Brain, visual: "kpi",
    content: [
      "The Area report ranks the 3 actions with the biggest \u00a3 impact. Each shows Finding \u2192 Evidence \u2192 Root cause \u2192 Action \u2192 Priority tier, plus a per-store list — never an average.",
      "Stores with no data are named explicitly (\u201cWinchburgh: no OSAT this week\u201d) rather than dropped.",
      "\u201cRefresh Coach\u201d regenerates in the background (extended thinking + a self-check that rewrites any action without a number) — the cards fill in when ready.",
      "Ask Operations: \u201cWhich store has the worst labour this week?\u201d streams a ranked answer across all 5 stores. Thumbs up/down every answer.",
    ],
    tip: "Click any store card to see that store\u2019s own report and chat exactly as the SM sees it." },

  { title: "Settings — Users & Logins", icon: LayoutDashboard, visual: "admin",
    content: [
      "HELP \u2192 Users & Logins is the one place for access control.",
      "Back Office Accounts — create Area Managers / Super Admins, set temp passwords, activate/deactivate, reset 2FA.",
      "Store Logins — set each store\u2019s username (default: store code, e.g. \u201ccur\u201d), reset its password (8+ chars with upper, lower, number and special), and flip the switch to disable/enable its login. Disabling signs that store out everywhere.",
      "Locked Accounts — unlock anyone who hit the 5-attempt lockout. Trusted Devices — see and revoke store devices.",
    ],
    tip: "Passwords are stored hashed and can never be viewed — only reset." },

  { title: "Dashboard — Submission Stats", icon: LayoutDashboard, visual: "admin",
    content: [
      "Click 'DASHBOARD' in the nav. At the top you'll see cards showing:",
      "Total Submissions — How many submissions have been made overall.",
      "Pending — Submissions waiting for your review. Check these first each morning.",
      "Approved — Already reviewed and confirmed as accurate.",
      "Mismatches — Submissions where OCR data doesn't match expected patterns. These need manual checking.",
    ],
    tip: "If 'Pending' is high, prioritise reviewing them — stores can't see their KPI rankings until data is approved." },

  { title: "Dashboard — Pasting Data (Part 1)", icon: LayoutDashboard, visual: "admin",
    content: [
      "Scroll down on the Dashboard to find the 'Paste Data' section. It has 6 tabs:",
      "90 DAYS — Paste 90-day sales comparison data from the regional Excel report.",
      "OTG TRACKER — Paste Out The Gate delivery times from the tracking spreadsheet.",
      "CURRENT WEEK — Paste this week's summary numbers if you have them from another source.",
      "The process for each tab is the same: Copy rows from Excel → Paste into the text box → Click 'Preview' to check → Click 'Save Data' to import.",
    ],
    tip: "ALWAYS click 'Preview' before saving. It shows you exactly what will be imported so you can spot any parsing errors." },

  { title: "Dashboard — Pasting Data (Part 2)", icon: LayoutDashboard, visual: "admin",
    content: [
      "The remaining 3 paste tabs:",
      "OSA — Paste Operations Standards Assessment scores. This feeds the 150-point OSA rating on each store's KPI.",
      "OSAT METRICS — Paste the satisfaction percentage table (Overall, Taste, Temperature, Accuracy, Appearance, Time to Receive). This is the main OSAT data table from your report.",
      "OSAT COMMENTS — Paste individual customer feedback comments. These appear on the Feedback page under the OSAT tab, grouped by satisfaction level.",
    ],
    tip: "OSAT Metrics and OSAT Comments are different data — Metrics are the percentage scores, Comments are the actual text feedback." },

  { title: "Reviewing Submissions", icon: Upload, visual: "submit",
    content: [
      "Go to 'SUBMISSIONS' in the nav. You'll see a list of every submission from all stores.",
      "Each row shows: Store code, Date, Manager name, Status (Pending/Approved/Rejected), and when it was submitted.",
      "Click on any submission to open the detail view. You'll see the uploaded images side-by-side with the OCR-extracted data.",
      "Check if the numbers look reasonable. If they look correct, click 'Approve'. If something is clearly wrong, click 'Reject' and the store will need to resubmit.",
    ],
    tip: "Approve submissions quickly — this data feeds KPI rankings, bonus calculations, and the War Zone in real-time." },

  { title: "KPI — All Stores Comparison", icon: BarChart3, visual: "kpi",
    content: [
      "Go to 'KPI' in the nav. As admin, you have a store selector dropdown at the top-left.",
      "Select any specific store (FAL, CUR, GRA, LIN, WIN) to see their detailed KPI view — exactly what the store manager sees.",
      "Select 'ALL' to see a comparison dashboard showing every store's metrics side-by-side.",
      "This is the best way to identify which stores are performing well and which need support.",
    ],
    tip: "Use the 'All Stores' view in your weekly area meeting to discuss performance across the board." },

  { title: "Setting Targets", icon: BarChart3, visual: "kpi",
    content: [
      "On the KPI page, click 'SET TARGETS' button in the top-right area.",
      "A panel opens where you can enter daily sales targets, weekly labour % targets, and other KPI targets for the selected store and week.",
      "Once saved, stores see their actual performance plotted against these targets on their charts and daily breakdown table.",
      "Targets drive the colour coding: metrics within target show green, metrics outside show red.",
    ],
    tip: "Set realistic but challenging targets. If every cell is green, targets might be too easy." },

  { title: "Manual Override", icon: BarChart3, visual: "kpi",
    content: [
      "Sometimes the OCR misreads a number from a photo. That's what Manual Override is for.",
      "Click 'MANUAL OVERRIDE' on the KPI page. Select the date and enter the correct sales, labour, OTD, or GPS ADT values.",
      "The override replaces the OCR-extracted data for that specific day. The rest of the week's data stays unchanged.",
      "The updated numbers immediately reflect in all charts, rankings, and the War Zone.",
    ],
    tip: "Only override when you're sure the OCR was wrong. Check the original photo in the Submissions detail view first." },

  { title: "Labour Plan — Multi-file Upload", icon: Upload, visual: "pdf",
    content: [
      "PERFORMANCE \u2192 Labour tab \u2192 Upload: drop ALL your Insider Performance, Driver Performance and Schedule Analysis .xls files at once.",
      "Each file\u2019s store and report type is read from its filename (e.g. Driver_Performance-2026-07-13_Falkirk.xls \u2192 FAL \u00b7 Driver). Schedule exports cover all stores.",
      "A per-file checklist shows what was detected; anything unrecognised is flagged instead of being saved to the wrong store.",
      "Hit \u201cLooks right \u2014 Save N files\u201d and every store\u2019s weeks land in one go.",
    ],
    tip: "Keep the original filenames from the export — they are how the app knows which store is which." },

  { title: "Weather — Per-Store Forecasts", icon: CloudSun, visual: "weather",
    content: [
      "The weather bar on the KPI page shows precise forecasts for each store's EXACT location — not a generic area forecast.",
      "When you switch stores in the dropdown, the weather updates to show that specific town's conditions and temperature.",
      "CUR (Edinburgh-Currie), FAL (Falkirk), LIN (Linlithgow), WIN (Winchburgh), GRA (Grangemouth) — each has its own GPS coordinates.",
      "The impact prediction (Peak/Good/Dip/Low) helps you anticipate order volume for each store individually.",
    ],
    tip: "Weather can vary between stores — Edinburgh might be sunny while Falkirk has rain. Always check per-store." },

  { title: "Business Calendar", icon: Calendar, visual: "weather",
    content: [
      "Below the weather is the Business Calendar. Click to expand it.",
      "It shows 21 days of upcoming events: Premier League, Champions League, Scottish Premiership, Hearts, Hibs, Falkirk FC, Edinburgh Rugby, UFC PPV, Boxing, Six Nations, Super Bowl, Wimbledon, pay weekends, bank holidays, Edinburgh Fringe, and more.",
      "Use the category filter pills to focus on specific types: Sports, Local Sports, UFC/Boxing, Pay Weekend, Local Event, Bank Holiday.",
      "Events with a fire icon are historically the biggest order drivers.",
    ],
    tip: "Review the calendar every Monday morning. Flag peak events to all stores so they can prepare." },

  { title: "Customer Feedback — Admin View", icon: AlertTriangle, visual: "feedback",
    content: [
      "Go to 'FEEDBACK'. As admin, you see ALL stores' data by default.",
      "Complaints tab: Use store filter pills (CUR, FAL, GRA, etc.) to drill into specific stores. The analytics dashboard shows category trends, repeat offenders, and which stores have the most complaints.",
      "OSAT tab: Click a store pill and the metric percentages (Satisfied/Not Highly Satisfied/Not Satisfied) recalculate live for that specific store. Comments filter too.",
      "You can manually add complaints using the '+' button on the Complaints tab.",
    ],
    tip: "The 'Repeat Issues' section flags stores with the same complaint type recurring 3+ weeks — address these urgently." },

  { title: "Weekly Tracker & War Zone", icon: Swords, visual: "warzone",
    content: [
      "TRACKER: A grid showing all stores' weekly performance. Each cell is colour-coded (green/amber/red). The 'DROP' column shows exactly how much each store needs to improve.",
      "WAR ZONE: A competitive heatmap ranking all 5 stores across every metric. Most green cells = best store overall.",
      "Both views are powerful tools for your weekly area meeting — Tracker shows improvement trends, War Zone shows current standings.",
    ],
    tip: "Share the War Zone in the managers' WhatsApp group every Monday to drive competition." },

  { title: "PDF Reports & Bonus Tracker", icon: Trophy, visual: "bonus",
    content: [
      "Click 'DOWNLOAD REPORT' on any store's KPI page to generate a professional PDF. It includes: sales summary, daily breakdown, OTG analysis, complaint review, and auto-generated action plan.",
      "The floating PacMan bar at the bottom shows quarterly bonus progress. Each store sees their own. PacMan eats letters as sales grow towards the quarterly target.",
      "Bonus levels (L1, L2, L3) unlock automatically. The HURRRR!!! animation plays when a milestone is hit.",
      "Download PDFs before your weekly area review — they summarise everything in a printable format.",
    ],
    tip: "The PDF action plan auto-generates improvement suggestions based on actual numbers — great for one-on-one store manager coaching." },

  { title: "Domi-Grill — Streak Race Admin", icon: Flame, visual: "warzone",
    content: [
      "Domi-Grill is a year-long weekly streak race. Every store competes to hit their daily sales target. Hit it → flame stays lit. Miss it → streak resets.",
      "As admin you can adjust the daily target curve (RaceView has a calendar date picker that snaps to that week's Monday).",
      "The winning store each week earns a permanent badge. Use this in your weekly area meeting to celebrate consistent performers.",
      "No data entry needed — Domi-Grill reads from the same KPI sales feed automatically.",
    ],
    tip: "Streak resets every Monday. Make sure stores submit by Sunday night so they don't accidentally lose their flame." },

  { title: "Domi-Vision — Upload Scores & PDFs", icon: BarChart3, visual: "pdf",
    content: [
      "Go to 'DOMI-VISION' in the nav. You'll see the admin console.",
      "Live toggle: flip Domi-Vision ON/OFF for stores. While OFF, store users see a placeholder 'Visit is happening' — useful during the rollout phase.",
      "Bulk-paste scores: paste the daily 22-column scoring export (Date, Store, Remake/Coach/Great counts, defect categories, image integrity, pizza coverage). The parser handles defects, quality, and percentages automatically.",
      "Upload weekly photos: drag the weekly PDF into the photo uploader. The server extracts every embedded pizza photo and saves each one as its own gallery tile — the raw PDF is never shown.",
      "Set the target sell %: 92% is the default (industry global standard). Adjust per-network if needed.",
    ],
    tip: "Always click 'Preview' before saving paste data. The parser shows defect counts and image-integrity %s row-by-row so you can catch any column mismatches." },

  { title: "Complaints Insights & Sun-Sat Bonus", icon: AlertTriangle, visual: "feedback",
    content: [
      "On the Feedback page, the new 'Insights' tab opens a yearly analytics dashboard: monthly heatmap, store leaderboard, repeat customers, defect categories, clean weeks, and a sales-correlation chart.",
      "Complaints paste is now ADDITIVE — re-pasting the same data merges into existing rows (dedupe by store + customer + date) instead of overwriting. Use the new Preview dialog to validate large imports before save.",
      "Complaint week boundaries are Sun-Sat for the bonus calc (matching the bonus rule), while sales/labour stay Mon-Sun. The Insights dashboard respects both.",
      "Help survey: a separate anonymous 10-question form. Admin sees aggregated NPS, breakdowns, and raw open comments inside the Help → Admin tab.",
    ],
    tip: "Watch the 'Repeat Issues' panel — if the same complaint category appears 3+ weeks in a row at one store, it's a process issue, not a one-off." },
];

const STEP_COLORS = {
  welcome: "from-[#0071e3] to-[#004d6e]",
  nav: "from-slate-800 to-slate-900",
  weather: "from-sky-900 to-slate-900",
  kpi: "from-[#0071e3] to-slate-900",
  charts: "from-indigo-900 to-slate-900",
  bonus: "from-amber-900 to-slate-900",
  pdf: "from-emerald-900 to-slate-900",
  feedback: "from-teal-900 to-slate-900",
  tracker: "from-amber-900 to-slate-900",
  warzone: "from-red-900 to-slate-900",
  submit: "from-emerald-900 to-slate-900",
  admin: "from-violet-900 to-slate-900",
};

export default function TutorialPopup({ isAdmin, isStore, onClose, initialStep = 0 }) {
  const [step, setStep] = useState(initialStep);

  const steps = isAdmin ? ADMIN_TUTORIAL : STORE_TUTORIAL;
  const current = steps[step];
  const isLast = step === steps.length - 1;
  const Visual = VISUAL_MAP[current.visual];

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="p-0 gap-0 max-w-lg border-0 overflow-hidden rounded-lg max-h-[85vh] flex flex-col" data-testid="tutorial-popup">
        <VisuallyHidden>
          <DialogTitle>{current.title}</DialogTitle>
          <DialogDescription>Tutorial — step {step + 1} of {steps.length}</DialogDescription>
        </VisuallyHidden>
        {/* Header */}
        <div className={`relative bg-gradient-to-br ${STEP_COLORS[current.visual] || STEP_COLORS.welcome} px-5 pt-4 pb-3 flex-shrink-0`}>
          <button data-testid="tutorial-skip" onClick={onClose} className="absolute top-3 right-3 text-white/40 hover:text-white/80 transition-colors">
            <X size={18} />
          </button>
          <div className="flex items-center gap-1 mb-2.5">
            {steps.map((_, i) => (
              <div key={i} className={`h-1 rounded-full transition-all duration-300 ${
                i === step ? "flex-[2] bg-white" : i < step ? "flex-1 bg-white/40" : "flex-1 bg-white/15"
              }`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {current.icon && <current.icon size={18} className="text-white/70" />}
            <h2 className="text-base font-black text-white uppercase tracking-wide" style={{ fontFamily: 'Inter, sans-serif' }}>
              {current.title}
            </h2>
          </div>
          <div className="text-[10px] text-white/40 mt-0.5 font-mono">Step {step + 1} of {steps.length}</div>
        </div>

        {/* Animated Visual + Content (scrollable) */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-5 pt-3" key={step}>
            {Visual && <Visual />}
          </div>

          {/* Content */}
          <div className="px-5 py-3">
            <div className="space-y-2">
              {current.content.map((text, i) => (
                <p key={i} className="text-[13px] text-slate-600 leading-relaxed flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0071e3] mt-1.5 flex-shrink-0" />
                  <span>{text}</span>
                </p>
              ))}
            </div>
            {current.tip && (
              <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                <span className="text-amber-500 text-sm mt-px flex-shrink-0">*</span>
                <p className="text-[11px] text-amber-800 leading-relaxed"><span className="font-bold">Pro tip:</span> {current.tip}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-2.5 bg-slate-50 border-t border-slate-100 flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)} disabled={step === 0}
            className="text-xs text-slate-400 gap-1" data-testid="tutorial-prev">
            <ChevronLeft size={14} /> Back
          </Button>
          <button onClick={onClose} className="text-[10px] text-slate-400 hover:text-slate-600 transition-colors underline underline-offset-2" data-testid="tutorial-dismiss">
            Skip tutorial
          </button>
          <Button size="sm" onClick={() => isLast ? onClose() : setStep(s => s + 1)}
            className="text-xs bg-[#0071e3] hover:bg-[#0060c0] text-white gap-1 rounded-md" data-testid="tutorial-next">
            {isLast ? "Get Started" : "Next"} {!isLast && <ChevronRight size={14} />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
